import * as React from 'react'
import { styled } from 'next-yak'

import * as glyphs from '../icons/glyphs'
import * as S from '../styles'

type BaseProps = {
  children: React.ReactNode
  className?: string
  closedContent?: React.ReactNode
  id?: string
  title?: React.ReactNode
  alwaysOpen?: boolean
  customTitle?: React.ReactNode
  animated?: boolean
}

type BoxedExtraProps = {
  type?: 'boxed'
  hideTopSeparator?: never
  hideBottomSeparator?: never
}

type StrokedExtraProps = {
  type: 'stroked'
  hideTopSeparator?: boolean
  hideBottomSeparator?: boolean
}

type ControlledExtraProps = {
  onToggle: () => void
  onToggleIcon?: () => void
  open: boolean
}

type UncontrolledExtraProps = {
  onToggle?: undefined
  onToggleIcon?: undefined
  open?: undefined
}

type AccordionProps = BaseProps &
  (BoxedExtraProps | StrokedExtraProps) &
  (ControlledExtraProps | UncontrolledExtraProps)

function AccordionComponent({
  children,
  className,
  closedContent,
  id,
  onToggle,
  onToggleIcon,
  open,
  title,
  type = 'boxed',
  hideTopSeparator = false,
  hideBottomSeparator = false,
  alwaysOpen = false,
  customTitle,
  animated = false,
}: AccordionProps): React.ReactElement<AccordionProps> {
  const stableRandomId = React.useId()
  const stableId = id ?? stableRandomId

  const contentRef = React.useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = React.useState<number>(0)

  React.useLayoutEffect(() => {
    const content = contentRef.current
    if (!content) return

    const updateHeight = () => setContentHeight(content.scrollHeight + 30)
    updateHeight()

    const observer = new ResizeObserver(updateHeight)
    observer.observe(content)

    return () => {
      observer.disconnect()
    }
  }, [contentRef.current])

  const handleIconClick = (event: React.MouseEvent) => {
    if (onToggleIcon) {
      event.preventDefault()
      onToggleIcon()
    }
  }

  return (
    <S.accordion.Wrapper
      className={className}
      $type={type}
      $hideTopSeparator={hideTopSeparator}
      $hideBottomSeparator={hideBottomSeparator}
      $alwaysOpen={alwaysOpen}
      $animated={animated}
      $height={contentHeight}
    >
      {!alwaysOpen && <S.accordion.Toggler type="checkbox" id={stableId} checked={open} onChange={onToggle} />}
      <S.accordion.Title htmlFor={stableId} $type={type}>
        {title && <S.copy.M.semiBold as="h3">{title}</S.copy.M.semiBold>}
        {customTitle && <>{customTitle}</>}
        {!alwaysOpen && (
          <>
            <glyphs.stroke.CaretDown onClick={handleIconClick} />
            <glyphs.stroke.CaretUp onClick={handleIconClick} />
          </>
        )}
      </S.accordion.Title>
      <S.accordion.Content $open={alwaysOpen ? true : open} $animated={animated} $height={contentHeight}>
        <div ref={contentRef}>{children}</div>
      </S.accordion.Content>
      {!alwaysOpen && closedContent && <S.accordion.ClosedContent>{closedContent}</S.accordion.ClosedContent>}
    </S.accordion.Wrapper>
  )
}
export const Accordion = styled(AccordionComponent)``
